import React from 'react';
import { Card, CardActions, CardContent, Grid } from '@material-ui/core';
import type { Theme } from '@material-ui/core/styles';

import { ActionButton, Heading } from '@klp/design';
import { Illustration } from '@klp/design/Illustrations';
import type { IconName, PictogramNameType } from '@klp/design/Illustrations';
import { useKlpStyles } from '../Theme/useKlpStyles';

export type FrontpageCardProps = {
    title: string;
    icon?: IconName;
    pictogram?: PictogramNameType;
    actionRoute?: string;
    actionClickHandler?: () => void;
    actionText?: string;
    external?: boolean;
    klpPalette?: string;
    children?: React.ReactNode;
    visible?: boolean;
};

const styles = (theme: Theme) => ({
    cardContentGrid: {
        justifyContent: 'space-between',
        alignItems: 'flex-end',
    },
    card: {
        padding: theme.spacing(2),
        // minHeight: theme.spacing(55),
    },
    cardActions: {
        marginTop: theme.spacing(4),
    },
});

export const FrontpageCard: React.FC<
    React.PropsWithChildren<FrontpageCardProps>
> = ({
    title,
    icon,
    pictogram,
    actionRoute,
    actionClickHandler,
    actionText,
    external,
    children,
    visible = true,
    ...rest
}) => {
    const classes = useKlpStyles({}, styles);

    if (!visible) return null;

    return (
        <Card className={classes.card} elevation={2} {...rest}>
            <CardContent>
                <Grid container={true} className={classes.cardContentGrid}>
                    <Grid item={true}>
                        <Heading klpHeadingLevel="THREE" translation={title} />
                    </Grid>
                    <Grid item={true}>
                        <Illustration
                            icon={icon}
                            pictogram={pictogram}
                            klpPalette="secondary"
                            klpColor="dark"
                            size="small"
                            data-testid="FrontpageCard-Illustration"
                        />
                    </Grid>
                </Grid>
                {children}
            </CardContent>
            {actionText && actionRoute && (
                <CardActions className={classes.cardActions}>
                    <ActionButton
                        actionType={external ? 'external' : 'button'}
                        translation={actionText}
                        target={actionRoute}
                        onClick={actionClickHandler}
                        klpTypographyVariant="small"
                    />
                </CardActions>
            )}
        </Card>
    );
};
